import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import Logo from "../../assets/logo/logo.png";

interface IRedirectPopupProps {
  link: string;
  content: string | React.ReactNode;
  target?: string;
  rel?: string;
  className?: string;
  type?: "button" | "link";
  arialabel?: string;
  key?: string;
}

const INTERNAL_DOMAINS = [
  "lilly.com",
  "lillyhub.com",
  "e.lilly",
  ".lilly",
  "gatewaylabs.lilly.com",
  "privacynotice.lilly.com",
];

const isExternalLink = (link: string): boolean => {
  if (link.startsWith("/") || link.startsWith("#")) {
    return false;
  }
  try {
    const url = new URL(link, window.location.href);
    const hostname = url.hostname.toLowerCase();
    return !INTERNAL_DOMAINS.some(
      (domain) => hostname.endsWith(domain) || hostname === domain,
    );
  } catch {
    return false;
  }
};

const RedirectPopup = ({
  link,
  content,
  target,
  rel,
  className,
  arialabel,
  key,
  type,
}: IRedirectPopupProps) => {
  const lillyLogoUrl = Logo;

  const [open, setOpen] = useState(false);
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isExternalLink(link)) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleContinue = () => {
    window.open(link, target ?? "_self");
    setOpen(false);
  };

  return (
    <>
      <a
        href={link}
        key={key ? key : link}
        target={target}
        rel={rel}
        role={type === "button" ? "button" : "link"}
        className={`${className} ${type === "button" ? "inline-block" : ""} cursor-pointer`}
        onClick={handleClick}
        aria-label={
          arialabel || (typeof content === "string" ? content : undefined)
        }
      >
        {content}
      </a>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[90vw] sm:w-[80vw] md:w-[70vw] lg:w-[32rem] max-w-[40rem] border border-border-gray20 p-6 sm:p-8 md:p-10 bg-white !rounded-[0.25rem] overflow-hidden">
          <div className="pt-8 sm:pt-10 md:pt-14 flex flex-col h-full">
            {lillyLogoUrl && (
              <img
                src={lillyLogoUrl}
                alt="Lilly Logo"
                className="mx-auto h-10 sm:h-12"
                style={{ maxWidth: "5.5rem" }}
              />
            )}

            <DialogTitle className="text-center text-xl sm:text-2xl md:text-[2rem] font-semibold break-words pt-8 sm:pt-10 md:pt-12">
              You are now leaving explornd.lilly.com
            </DialogTitle>
            <div className="flex flex-col gap-8">
              <div className="text-center text-sm sm:text-base pt-4 sm:pt-6">
                You've clicked on a link for a third-party site, which Lilly
                does not control, influence, or endorse. Lilly is not
                responsible for any content on this third-party site, including
                the privacy policy. Click "Continue" to leave or "Go Back" to
                return to explornd.lilly.com.
              </div>
              <div className="flex justify-center gap-4 sm:gap-8 pt-8 sm:pt-10 md:pt-12 pb-4">
                <Button
                  variant="outline"
                  className="rounded-[0.25rem] text-base sm:text-lg font-bold"
                  onClick={() => setOpen(false)}
                >
                  Go Back
                </Button>
                <Button
                  variant={"secondary"}
                  className="rounded-[0.25rem] text-base sm:text-lg font-bold"
                  onClick={handleContinue}
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RedirectPopup;
